import React from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
} from '@mui/material';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import CheckIcon from '@mui/icons-material/Check';
import { useAppTheme } from '../../theme/ThemeContext';
import { ThemeName, themeAccentColors } from '../../theme/palette';

const themeDisplayNames: Record<ThemeName, string> = {
  default: '暖调',
  forest: '森林',
  ocean: '海洋',
  lavender: '薰衣草',
  mono: '水墨',
  glass: '玻璃',
};

const ThemeSwitcher: React.FC = () => {
  const { mode, toggleTheme, effectiveMode, colorTheme, setColorTheme } = useAppTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleModeChange = (newMode: 'light' | 'dark' | 'system') => {
    toggleTheme(newMode);
    handleMenuClose();
  };

  const handleColorThemeChange = (theme: ThemeName) => {
    setColorTheme(theme);
  };

  return (
    <>
      <IconButton
        onClick={handleMenuOpen}
        color="inherit"
        aria-label="切换主题"
        sx={{
          ml: 1,
          transition: 'transform 0.2s ease',
          '&:hover': { transform: 'scale(1.1)' },
          '&:active': { transform: 'scale(0.95)' },
        }}
      >
        {effectiveMode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { minWidth: 200 },
          },
        }}
      >
        {/* Color Theme Section */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600, display: 'block', mb: 1 }}
          >
            配色主题
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {(Object.keys(themeAccentColors) as ThemeName[]).map((theme) => {
              const accentColor = themeAccentColors[theme][effectiveMode];
              const isSelected = colorTheme === theme;
              return (
                <Box
                  key={theme}
                  onClick={() => handleColorThemeChange(theme)}
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    backgroundColor: accentColor,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: isSelected ? '3px solid' : '2px solid',
                    borderColor: isSelected ? 'text.primary' : 'divider',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      boxShadow: 2,
                    },
                  }}
                  title={themeDisplayNames[theme]}
                >
                  {isSelected && (
                    <CheckIcon
                      sx={{
                        fontSize: 16,
                        color: 'common.white',
                        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.4))',
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Light/Dark Mode Section */}
        <MenuItem onClick={() => handleModeChange('light')} selected={mode === 'light'}>
          <ListItemIcon>
            <Brightness7Icon fontSize="small" />
          </ListItemIcon>
          <ListItemText>浅色模式</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleModeChange('dark')} selected={mode === 'dark'}>
          <ListItemIcon>
            <Brightness4Icon fontSize="small" />
          </ListItemIcon>
          <ListItemText>深色模式</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleModeChange('system')} selected={mode === 'system'}>
          <ListItemIcon>
            <SettingsBrightnessIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>跟随系统</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default ThemeSwitcher;
